import { SlateMark } from '@all-hands-hq/plex-graphql-schema';
import { Button, makeStyles, Menu, MenuItem, Popover } from '@material-ui/core';
import FormatCodeIcon from '@material-ui/icons/Code';
import FormatBoldIcon from '@material-ui/icons/FormatBold';
import FormatItalicIcon from '@material-ui/icons/FormatItalic';
import FormatListBulletedIcon from '@material-ui/icons/FormatListBulleted';
import FormatListNumberedIcon from '@material-ui/icons/FormatListNumbered';
import FormatQuoteIcon from '@material-ui/icons/FormatQuote';
import FormatUnderlinedIcon from '@material-ui/icons/FormatUnderlined';
import LinkIcon from '@material-ui/icons/Link';
import LooksOneIcon from '@material-ui/icons/LooksOne';
import LooksTwoIcon from '@material-ui/icons/LooksTwo';
import { addColumn, addRow, deleteColumn, deleteRow, deleteTable, insertTable } from '@udecode/slate-plugins';
import classNames from 'classnames';
import React, { useState } from 'react';
import { ReactEditor } from 'slate-react';

import { newLinkEditorMenu } from '../../../../actions/newLinkEditorMenu';
import { ReactComponent as AttachementSvg } from '../../../../assets/attachement.svg';
import { ReactComponent as PlexLogoSvg } from '../../../../assets/plex_logo.svg';
import { useDispatch } from '../../../../redux';
import { colorPalette, spacings } from '../../../../theme';
import { SearchResultsPageSlate } from '../../../logged_in/search/SearchResultsPageSlate';
import { SearchResultOption } from '../../../logged_in/search/useSearch';
import { ButtonColor, PlexButton } from '../../primitives/PlexButton';
import { SlatePluginOptions } from './plugins';
import { useSlate } from './useSlate';
import { Format, HeadingStyle, isBlockActive, isMarkActive, toggleBlock, toggleMark } from './utils';
import { canLinkifySelection, insertFiles } from './withLinks';

type BlockButtonProps = {
  format: Format;
  headingStyle?: HeadingStyle;
  icon: React.ReactElement;
};

export function BlockButton({ format, headingStyle, icon }: BlockButtonProps): React.ReactElement {
  const editor = useSlate();
  const classes = useStyles();
  const labelStyle = isBlockActive(editor, { format, headingStyle })
    ? classes.activeButtonLabel
    : classes.inactiveButtonLabel;
  return (
    <Button
      onMouseDown={(event): void => {
        // Prevent making the button active which would cause loss of focus of the editor
        event.preventDefault();

        toggleBlock(editor, { format, headingStyle });
      }}
      classes={{ label: classNames(classes.buttonLabel, labelStyle), root: classes.button }}
    >
      {icon}
    </Button>
  );
}

type MarkButtonProps = {
  format: SlateMark;
  icon: React.ReactElement;
};

export function MarkButton({ format, icon }: MarkButtonProps): React.ReactElement {
  const editor = useSlate();
  const classes = useStyles();
  const labelStyle = isMarkActive(editor, format) ? classes.activeButtonLabel : classes.inactiveButtonLabel;
  return (
    <Button
      onMouseDown={(event): void => {
        // Prevent making the button active which would cause loss of focus of the editor
        event.preventDefault();
        toggleMark(editor, format);
      }}
      classes={{ label: classNames(classes.buttonLabel, labelStyle), root: classes.button }}
    >
      {icon}
    </Button>
  );
}

function LinkButton(): React.ReactElement {
  const editor = useSlate();
  const classes = useStyles();
  const dispatch = useDispatch();

  const disabled = editor.selection == null || !canLinkifySelection(editor, editor.selection);
  const labelStyle = disabled ? undefined : classes.inactiveButtonLabel;

  const handleClick = (): void => {
    if (editor.selection != null) {
      dispatch(newLinkEditorMenu(editor.selection));
    }
  };

  return (
    <Button
      disabled={disabled}
      onClick={handleClick}
      classes={{ label: classNames(classes.buttonLabel, labelStyle), root: classes.button }}
    >
      <LinkIcon />
    </Button>
  );
}

function TableButton(): React.ReactElement {
  const editor = useSlate();
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const onInsertTable = (): void => {
    insertTable(editor, SlatePluginOptions);
    handleClose();
  };

  const onDeleteTable = (): void => {
    deleteTable(editor, SlatePluginOptions);
    handleClose();
  };

  const onAddRow = (): void => {
    addRow(editor, SlatePluginOptions);
    handleClose();
  };

  const onRemoveRow = (): void => {
    deleteRow(editor, SlatePluginOptions);
    handleClose();
  };

  const onAddColumn = (): void => {
    addColumn(editor, SlatePluginOptions);
    handleClose();
  };

  const onRemoveColumn = (): void => {
    deleteColumn(editor, SlatePluginOptions);
    handleClose();
  };

  const handleClose = () => {
    setAnchorEl(null);
    ReactEditor.focus(editor);
  };

  const isTableActive = isBlockActive(editor, { format: 'table' });
  const labelStyle = isTableActive ? classes.activeButtonLabel : classes.inactiveButtonLabel;
  return (
    <>
      <Button
        classes={{ label: classNames(classes.buttonLabel, labelStyle), root: classes.button }}
        onClick={handleClick}
      >
        Table
      </Button>
      <Menu id="table-action-menu" anchorEl={anchorEl} keepMounted open={Boolean(anchorEl)} onClose={handleClose}>
        <MenuItem onClick={onInsertTable} disabled={isTableActive}>
          Add table
        </MenuItem>
        <MenuItem onClick={onDeleteTable} disabled={!isTableActive}>
          Remove table
        </MenuItem>
        <MenuItem onClick={onAddRow} disabled={!isTableActive}>
          Add Row
        </MenuItem>
        <MenuItem onClick={onRemoveRow} disabled={!isTableActive}>
          Remove Row
        </MenuItem>
        <MenuItem onClick={onAddColumn} disabled={!isTableActive}>
          Add column
        </MenuItem>
        <MenuItem onClick={onRemoveColumn} disabled={!isTableActive}>
          Remove column
        </MenuItem>
      </Menu>
    </>
  );
}

function AttachmentButton(): React.ReactElement {
  const classes = useStyles();
  const editor = useSlate();

  return (
    <React.Fragment>
      <input
        type="file"
        multiple
        onChange={(ev): void => {
          if (ev.currentTarget.files != null) insertFiles(editor, ev.currentTarget.files);
        }}
        className={classes.fileInput}
        id="text-editor-file-input"
      />
      <label htmlFor="text-editor-file-input">
        <Button
          classes={{ label: classNames(classes.buttonLabel, classes.inactiveButtonLabel), root: classes.button }}
          component="span"
          onFocus={(event: React.FocusEvent<HTMLButtonElement>): void => {
            event.preventDefault();
            ReactEditor.focus(editor);
          }}
        >
          <AttachementSvg />
        </Button>
      </label>
    </React.Fragment>
  );
}

export function SearchButton({
  onInsertSearchItems,
}: {
  onInsertSearchItems: (searchResult: SearchResultOption[]) => void;
}): React.ReactElement {
  const editor = useSlate();
  const classes = useStyles();
  const [searchOpen, setSearchOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<Element | null>(null);

  return (
    <>
      <PlexButton
        className={classes.searchButton}
        SvgIconLeft={PlexLogoSvg}
        label={'Add Plex Updates'}
        color={ButtonColor.Blue}
        onClick={(event?: React.MouseEvent<HTMLElement>): void => {
          if (!event) {
            console.error('could not open search because event is empty');
            return;
          }
          setAnchorEl(event.currentTarget);
          setSearchOpen(!searchOpen);
        }}
        onFocus={(event: React.FocusEvent<HTMLButtonElement>): void => {
          event.preventDefault();
          ReactEditor.focus(editor);
        }}
      />

      <Popover
        open={searchOpen}
        anchorEl={anchorEl}
        onClose={(): void => {
          setSearchOpen(false);
        }}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        // Preserve the editable text area focused
        disablePortal
      >
        <SearchResultsPageSlate
          onInsertSearchItems={onInsertSearchItems}
          closeSearch={(): void => {
            setSearchOpen(false);
          }}
        />
      </Popover>
    </>
  );
}

interface EditorButtonsProps {
  onInsertSearchItems: (searchResult: SearchResultOption[]) => void;
}

export function EditorButtons({ onInsertSearchItems }: EditorButtonsProps): React.ReactElement {
  const classes = useStyles();
  return (
    <div className={classes.buttonContainer}>
      <MarkButton format="bold" icon={<FormatBoldIcon />} />
      <MarkButton format="italic" icon={<FormatItalicIcon />} />
      <MarkButton format="underline" icon={<FormatUnderlinedIcon />} />
      <MarkButton format="code" icon={<FormatCodeIcon />} />
      <BlockButton format="paragraph" headingStyle="HEADING_1" icon={<LooksOneIcon />} />
      <BlockButton format="paragraph" headingStyle="HEADING_2" icon={<LooksTwoIcon />} />
      <BlockButton format="block-quote" icon={<FormatQuoteIcon />} />
      <BlockButton format="bulleted-list" icon={<FormatListBulletedIcon />} />
      <BlockButton format="numbered-list" icon={<FormatListNumberedIcon />} />
      <LinkButton />
      <AttachmentButton />
      <TableButton />
      <SearchButton onInsertSearchItems={onInsertSearchItems} />
    </div>
  );
}

const useStyles = makeStyles((theme) => ({
  buttonContainer: {
    display: 'flex',
    flex: '1',
    padding: spacings.small,
    borderTopWidth: 1,
    borderTopColor: colorPalette.purpleGray._100,
    borderTopStyle: 'solid',
  },
  fileInput: {
    display: 'none',
  },
  button: {
    padding: 0,
    borderRadius: 4,
    minWidth: 4,
  },
  buttonLabel: {
    paddingTop: spacings.small,
    paddingBottom: spacings.small,
    paddingLeft: spacings.small,
    paddingRight: spacings.small,
  },
  activeButtonLabel: {
    color: colorPalette.primaryColorBlue._500,
    fill: `${colorPalette.primaryColorBlue._500} !important`, // sets the svg color
  },
  inactiveButtonLabel: {
    color: colorPalette.purpleGray._500,
    fill: `${colorPalette.purpleGray._500} !important`, // sets the svg color
    '&:hover': {
      color: colorPalette.primaryColorBlue._500,
      fill: `${colorPalette.primaryColorBlue._500} !important`, // sets the svg color
    },
  },
  urlMenuItem: {
    width: '500px',
  },
  searchButton: {
    marginLeft: 'auto',
  },
}));
